import { useState, useEffect } from 'react';
import axios from 'axios';
import { Wine, Calendar, Clock, MapPin, LogOut, Image, Download, Share2, TrendingUp, Edit2, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import EventForm from '../components/EventForm';
import CountdownTimer from '../components/CountdownTimer';
import PullToRefresh from '../components/PullToRefresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MEMBERS = ['Ayrton', 'Andy', 'Alex', 'Ant', 'Daryl', 'Jack', 'Ryan', 'Leigh'];

const HomePage = ({ currentMember, onLogout }) => {
  const [events, setEvents] = useState([]);
  const [rsvps, setRsvps] = useState({});
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [expandedEvents, setExpandedEvents] = useState({});
  const [loading, setLoading] = useState(true);

  const isAdmin = currentMember?.toLowerCase() === 'ayrton';

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      const eventsData = response.data;
      setEvents(eventsData);

      // Fetch RSVPs for each event
      const rsvpPromises = eventsData.map((event) =>
        axios.get(`${API}/rsvp/${event.id}`)
      );
      const rsvpResponses = await Promise.all(rsvpPromises);

      const rsvpMap = {};
      eventsData.forEach((event, index) => {
        rsvpMap[event.id] = rsvpResponses[index].data;
      });
      setRsvps(rsvpMap);
      
      return true; // Success
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleRSVP = async (eventId, status) => {
    try {
      await axios.post(`${API}/rsvp`, {
        event_id: eventId,
        member_name: currentMember,
        status,
      });
      toast.success(`You're ${status === 'in' ? 'IN' : 'OUT'}!`);
      fetchEvents();
    } catch (error) {
      console.error('Error submitting RSVP:', error);
      toast.error('Failed to submit RSVP');
    }
  };

  const handleAddToCalendar = async (eventId) => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/calendar`, {
        responseType: 'blob',
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `long-island-event.ics`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Calendar event downloaded!');
    } catch (error) {
      console.error('Error downloading calendar:', error);
      toast.error('Failed to download calendar event');
    }
  };

  const handleShareWhatsApp = (event) => {
    const eventDate = new Date(event.date).toLocaleDateString('en-GB', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
    
    // Use production URL or custom domain if available
    const appUrl = 'https://longislands.uk'; // Will work once custom domain is set up
    
    const message = `Men of Long Island, ${event.title}

🗓️ ${eventDate}
🕐 ${event.time}
📍 ${event.location}

${appUrl}

Reply with 'IN' or 'NOT COMING' to be added to the fuck em toast 🍹`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
    toast.success('Opening WhatsApp...');
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setShowEventForm(true);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success('Event deleted!');
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    }
  };

  const toggleEventExpand = (eventId) => {
    setExpandedEvents(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  const getUpcomingEvent = () => {
    if (events.length === 0) return null;
    const now = new Date();
    const upcomingEvents = events.filter((event) => {
      const eventDate = new Date(`${event.date}T${event.time}`);
      return eventDate > now;
    });
    return upcomingEvents.length > 0 ? upcomingEvents[0] : null;
  };

  const getAllUpcomingEvents = () => {
    const now = new Date();
    return events
      .filter((event) => {
        const eventDate = new Date(`${event.date}T${event.time}`);
        return eventDate > now;
      })
      .slice(1); // Skip the first one (main event)
  };

  const upcomingEvent = getUpcomingEvent();
  const allUpcomingEvents = getAllUpcomingEvents();
  const eventRsvps = upcomingEvent ? rsvps[upcomingEvent.id] || [] : [];
  const attendees = eventRsvps.filter((r) => r.status === 'in');
  const notComing = eventRsvps.filter((r) => r.status === 'out');
  const currentUserRsvp = eventRsvps.find((r) => r.member_name === currentMember);

  // Calculate days until event for reminder
  const getDaysUntilEvent = () => {
    if (!upcomingEvent) return null;
    const now = new Date();
    const eventDate = new Date(`${upcomingEvent.date}T${upcomingEvent.time}`);
    const diffTime = eventDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntil = upcomingEvent ? getDaysUntilEvent() : null;
  const showReminder = upcomingEvent && isAdmin; // Show for admin whenever there's an upcoming event

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050203] flex items-center justify-center">
        <Wine className="w-12 h-12 text-[#E11D48] animate-pulse" />
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={fetchEvents}>
      <div className="min-h-screen bg-[#050203] relative overflow-hidden">
      {/* Background Texture */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: 'url(https://www.transparenttextures.com/patterns/black-felt.png)',
        }}
      />

      <div className="max-w-md mx-auto min-h-screen relative shadow-2xl border-x border-white/5">
        {/* Header */}
        <div className="bg-zinc-900/50 backdrop-blur-sm border-b border-white/10 p-6 sticky top-0 z-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Wine className="w-8 h-8 text-[#E11D48]" />
              <div>
                <h1 className="font-['Anton'] text-2xl text-white uppercase tracking-tight">
                  LONG ISLAND
                </h1>
                <p className="text-zinc-500 text-xs uppercase tracking-widest">
                  {currentMember}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/stats"
                data-testid="stats-link"
                className="p-2 text-zinc-400 hover:text-[#E11D48] transition-colors"
              >
                <TrendingUp className="w-6 h-6" />
              </Link>
              <Link
                to="/gallery"
                data-testid="gallery-link"
                className="p-2 text-zinc-400 hover:text-[#E11D48] transition-colors"
              >
                <Image className="w-6 h-6" />
              </Link>
              <button
                onClick={onLogout}
                data-testid="logout-button"
                className="p-2 text-zinc-400 hover:text-[#E11D48] transition-colors"
              >
                <LogOut className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Admin Event Reminder - Only for Ayrton */}
        {showReminder && (
          <div className="bg-zinc-900 border-2 border-[#E11D48] p-4 mx-6 mt-6 rounded-lg">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">⏰</span>
                  <p className="font-bold text-white uppercase tracking-wide text-sm">
                    Send Reminder to Group
                  </p>
                </div>
                <p className="text-zinc-300 text-sm">
                  {daysUntil === 0 && `Event TODAY at ${upcomingEvent.time}`}
                  {daysUntil === 1 && `Event tomorrow at ${upcomingEvent.time}`}
                  {daysUntil > 1 && daysUntil <= 7 && `Event this week at ${upcomingEvent.time}`}
                  {daysUntil > 7 && `Event in ${daysUntil} days at ${upcomingEvent.time}`}
                  {' - Don\'t forget to RSVP! 🍹'}
                </p>
              </div>
              <button
                data-testid="reminder-whatsapp-button"
                onClick={() => {
                  const eventDate = new Date(upcomingEvent.date).toLocaleDateString('en-GB', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  });
                  
                  const reminderMessage = `⏰ REMINDER: ${upcomingEvent.title}

🗓️ ${eventDate}
🕐 ${upcomingEvent.time}
📍 ${upcomingEvent.location}

https://longislands.uk

Don't forget to RSVP! Reply with 'IN' or 'NOT COMING' 🍹`;

                  const encodedMessage = encodeURIComponent(reminderMessage);
                  window.open(`https://api.whatsapp.com/send?text=${encodedMessage}`, '_blank');
                  toast.success('Opening WhatsApp with reminder...');
                }}
                className="bg-[#25D366] hover:bg-[#20BA5A] text-white px-4 py-2 rounded font-bold text-sm uppercase tracking-wider transition-colors flex items-center gap-2 whitespace-nowrap"
              >
                <Share2 className="w-4 h-4" />
                Post to WhatsApp
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6 space-y-12">
          {/* Admin Event Creation */}
          {isAdmin && (
            <div>
              {!showEventForm ? (
                <button
                  data-testid="create-event-button"
                  onClick={() => {
                    setEditingEvent(null);
                    setShowEventForm(true);
                  }}
                  className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white font-['Anton'] text-xl uppercase tracking-widest py-4 transition-colors border-2 border-transparent hover:border-white/20"
                >
                  + Create Event
                </button>
              ) : (
                <EventForm
                  onClose={() => {
                    setShowEventForm(false);
                    setEditingEvent(null);
                  }}
                  onEventCreated={fetchEvents}
                  editingEvent={editingEvent}
                />
              )}
            </div>
          )}

          {/* Upcoming Event */}
          {upcomingEvent ? (
            <div className="space-y-8">
              {/* Countdown */}
              <div className="text-center">
                <h2 className="font-['Anton'] text-4xl md:text-6xl text-white uppercase tracking-tight mb-6">
                  NEXT EVENT IN:
                </h2>
                <CountdownTimer targetDate={`${upcomingEvent.date}T${upcomingEvent.time}`} />
              </div>

              {/* Event Details */}
              <div className="bg-zinc-900/50 border border-white/10 backdrop-blur-sm p-6 relative overflow-hidden group hover:border-[#E11D48]/50 transition-colors">
                <div className="absolute top-4 right-4 font-['JetBrains_Mono'] text-4xl text-[#E11D48]/20">
                  #{upcomingEvent.event_number}
                </div>
                {isAdmin && (
                  <div className="absolute top-4 left-4 flex gap-2">
                    <button
                      onClick={() => handleEditEvent(upcomingEvent)}
                      className="p-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white rounded transition-colors"
                      title="Edit event"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteEvent(upcomingEvent.id)}
                      className="p-2 bg-zinc-800 hover:bg-red-900 text-zinc-400 hover:text-white rounded transition-colors"
                      title="Delete event"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <div className="space-y-4 mt-8">
                  <h3 className="font-['Anton'] text-3xl text-white uppercase tracking-wide pr-16">
                    {upcomingEvent.title}
                  </h3>
                  <div className="space-y-2 text-zinc-300">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-[#E11D48]" />
                      <span>{new Date(upcomingEvent.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-[#E11D48]" />
                      <span>{upcomingEvent.time}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-[#E11D48]" />
                      <span>{upcomingEvent.location}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <button
                      data-testid="add-to-calendar-button"
                      onClick={() => handleAddToCalendar(upcomingEvent.id)}
                      className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white border border-white/10 hover:border-[#E11D48]/50 py-3 px-4 transition-colors flex items-center justify-center gap-2 uppercase tracking-wider text-sm font-bold"
                    >
                      <Download className="w-4 h-4" />
                      Calendar
                    </button>
                    <button
                      data-testid="share-whatsapp-button"
                      onClick={() => handleShareWhatsApp(upcomingEvent)}
                      className="bg-[#25D366] hover:bg-[#20BA5A] text-white border border-transparent hover:border-white/20 py-3 px-4 transition-colors flex items-center justify-center gap-2 uppercase tracking-wider text-sm font-bold"
                    >
                      <Share2 className="w-4 h-4" />
                      WhatsApp
                    </button>
                  </div>
                </div>
              </div>

              {/* RSVP Section */}
              <div className="space-y-6">
                <h2 className="font-['Anton'] text-3xl text-white uppercase tracking-tight text-center">
                  BE A FREAK AND COME THIS WEEK:
                </h2>

                <div className="grid grid-cols-2 gap-4">
                  <button
                    data-testid="rsvp-in-button"
                    onClick={() => handleRSVP(upcomingEvent.id, 'in')}
                    disabled={currentUserRsvp?.status === 'in'}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-green-900 text-white font-bold text-xl py-6 uppercase tracking-tighter rounded-none transition-all hover:scale-105 disabled:scale-100"
                  >
                    I'M IN
                  </button>
                  <button
                    data-testid="rsvp-out-button"
                    onClick={() => handleRSVP(upcomingEvent.id, 'out')}
                    disabled={currentUserRsvp?.status === 'out'}
                    className="bg-zinc-800 hover:bg-red-900 disabled:bg-red-950 text-zinc-400 hover:text-white disabled:text-red-500 font-bold text-xl py-6 uppercase tracking-tighter rounded-none transition-all hover:scale-105 disabled:scale-100"
                  >
                    NOT COMING
                  </button>
                </div>

                {/* Attendees List */}
                {attendees.length > 0 && (
                  <div className="bg-zinc-900/30 border border-green-900/50 p-6">
                    <h3 className="font-['Anton'] text-xl text-green-400 uppercase mb-4">
                      Freaks going:
                    </h3>
                    <div data-testid="attendees-list" className="space-y-2">
                      {attendees.map((rsvp) => (
                        <div key={rsvp.id} className="text-white text-lg">
                          🍹 {rsvp.member_name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Toast List */}
                {notComing.length > 0 && (
                  <div
                    className="bg-zinc-900 p-8 rotate-1 shadow-xl border-2 border-white/5"
                    style={{
                      backgroundImage: 'url(https://www.transparenttextures.com/patterns/black-felt.png)',
                    }}
                  >
                    <h3 className="font-['Anton'] text-2xl text-[#E11D48] uppercase mb-6 tracking-wider">
                      Fuck 'em - Toast List:
                    </h3>
                    <div data-testid="toast-list" className="space-y-3">
                      {notComing.map((rsvp) => (
                        <div
                          key={rsvp.id}
                          className="font-['Anton'] text-2xl text-red-500 line-through decoration-white/30 decoration-2"
                        >
                          🖕🏼 {rsvp.member_name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-20">
              <Wine className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500 text-xl uppercase tracking-wider">
                No upcoming events
              </p>
              {isAdmin && (
                <p className="text-zinc-600 text-sm mt-2">Create one to get started!</p>
              )}
            </div>
          )}

          {/* All Other Upcoming Events */}
          {allUpcomingEvents.length > 0 && (
            <div className="border-t border-white/10 pt-8">
              <h2 className="font-['Anton'] text-2xl text-white uppercase tracking-tight mb-6 text-center">
                OTHER UPCOMING EVENTS
              </h2>
              <div className="space-y-3">
                {allUpcomingEvents.map((event) => {
                  const isExpanded = expandedEvents[event.id];
                  const eventRsvps = rsvps[event.id] || [];
                  const attendees = eventRsvps.filter((r) => r.status === 'in');

                  return (
                    <div
                      key={event.id}
                      className="bg-zinc-900/30 border border-white/10 overflow-hidden"
                    >
                      <div
                        className="p-4 cursor-pointer hover:bg-zinc-900/50 transition-colors"
                        onClick={() => toggleEventExpand(event.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <h3 className="font-['Anton'] text-xl text-white uppercase">
                                {event.title}
                              </h3>
                              {isExpanded ? (
                                <ChevronUp className="w-5 h-5 text-zinc-400" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-zinc-400" />
                              )}
                            </div>
                            <div className="text-zinc-400 text-sm mt-1">
                              {new Date(event.date).toLocaleDateString('en-GB', {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              })}
                              {' • '}
                              {event.time}
                            </div>
                            {attendees.length > 0 && (
                              <div className="text-green-400 text-xs mt-2">
                                {attendees.length} going
                              </div>
                            )}
                          </div>
                          <div className="font-['JetBrains_Mono'] text-xl text-[#E11D48]/50">
                            #{event.event_number}
                          </div>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-white/10 p-4 space-y-4 bg-zinc-900/20">
                          {isAdmin && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditEvent(event)}
                                className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 py-2 px-3 text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-colors"
                              >
                                <Edit2 className="w-4 h-4" />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteEvent(event.id)}
                                className="flex-1 bg-zinc-800 hover:bg-red-900 text-zinc-300 hover:text-white py-2 px-3 text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </div>
                          )}
                          <div className="text-zinc-300 text-sm">
                            <div className="flex items-center gap-2 mb-2">
                              <MapPin className="w-4 h-4 text-[#E11D48]" />
                              <span>{event.location}</span>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={() => handleAddToCalendar(event.id)}
                              className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 py-2 px-3 text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-colors"
                            >
                              <Download className="w-3 h-3" />
                              Calendar
                            </button>
                            <button
                              onClick={() => handleShareWhatsApp(event)}
                              className="bg-[#25D366] hover:bg-[#20BA5A] text-white py-2 px-3 text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-colors"
                            >
                              <Share2 className="w-3 h-3" />
                              WhatsApp
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </PullToRefresh>
  );
};

export default HomePage;